import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, ClickAwayListener, Tooltip } from '@mui/material';
import React, { useState } from 'react';

type ColumnInfoTooltipProps = {
  tooltip: string;
  /** When true, clicks on the icon do not bubble (e.g. inside a list row toggle). */
  stopPropagation?: boolean;
};

/** Row layout: label left, help icon right. */
export const columnLabelWithTooltipRowSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  minWidth: 0,
  gap: 0.5,
} as const;

/**
 * Subtle help icon for column tooltips. Opens on hover and click/tap.
 */
export function ColumnInfoTooltip({ tooltip, stopPropagation = false }: ColumnInfoTooltipProps) {
  const [open, setOpen] = useState(false);

  const icon = (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        title={tooltip}
        arrow
        open={open}
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 280,
              fontSize: '0.75rem',
              lineHeight: 1.4,
              bgcolor: 'grey.700',
              py: 0.75,
              px: 1,
              '& .MuiTooltip-arrow': {
                color: 'grey.700',
              },
            },
          },
          popper: {
            sx: { zIndex: 3000 },
          },
        }}
      >
        <HelpOutlineIcon
          onClick={() => setOpen((prev) => !prev)}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          data-testid="column-info-tooltip"
          aria-label="Column help"
          sx={{
            fontSize: 14,
            color: 'text.secondary',
            cursor: 'help',
            flexShrink: 0,
            '&:hover': {
              color: 'text.primary',
            },
          }}
        />
      </Tooltip>
    </ClickAwayListener>
  );

  if (!stopPropagation) {
    return icon;
  }

  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {icon}
    </Box>
  );
}
